import {
  DocumentInsightsSelection,
  EnsureDocumentInsightsRequest,
  SavedDocumentInsights,
} from '@listening/api-contract/document-insights';
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { OptionalUserAuthMiddleware, UserAuthMiddleware } from './middleware';
import { Effect, Schema, SchemaTransformation } from 'effect';
import { DocumentTypeSchema } from './schemas/document-type';
import {
  AudioConversionFinalizedMetadata,
  AudioConversionSummary,
  ConversionStatusSchema,
  GetConversionDefaultsV2200,
  GetConversionDiagnosticsV2200,
  GetGetAudioDetailedV2200,
  GetGetAudioV2200,
  GetListAudioChaptersV2200,
  GetListAudioV2200,
  PostCreateAudioV2200,
  SharingInfoVisibility,
} from './schemas/audio-dtos';
import {
  AudioRechunkingError,
  BadGatewayError,
  BadRequestError,
  DocumentInaccessibleError,
  FileTooLargeError,
  IntegrationReconnectRequiredError,
  InternalServerError,
  RateLimitedError,
  NotFoundError,
  ServiceUnavailableError,
  UploadDocumentTypeNotAllowedError,
  UploadQuotaExceededError,
  UnauthorizedError,
} from './errors';
import { VoiceInput } from './schemas/voice';
import {
  OFFLINE_DOC_PACK_MAX_CHUNKS,
  SYNTHESIZE_MAX_RANGES,
  SynthesizeDocPackRange,
} from './schemas/tts-dtos';

// URL params must be string-encodeable. The legacy schema uses Int which doesn't work.
const ListChaptersUrlParams = Schema.Struct({
  cursor: Schema.optional(Schema.String).annotate({
    description: 'Pagination cursor for the next page',
  }),
  limit: Schema.optional(Schema.FiniteFromString).annotate({
    description: 'Number of chapters to return (1-16, default 16)',
  }),
});

// URL params arrive as strings; transform the capability flag to a boolean.
const DocPackProgressBooleanFromString = Schema.String.pipe(
  Schema.decodeTo(
    Schema.Boolean,
    SchemaTransformation.transform({
      decode: (s) => s === 'true',
      encode: (b) => (b ? 'true' : 'false'),
    }),
  ),
);

// Shared client capability signal for every progress-returning endpoint
// (ListAudio, GetAudio, GetAudioDetailed carry it as this urlParam;
// UpdateAudio/FinalizeAudio carry it as an optional payload field). Optional =
// additive, freeze-safe. When `true`, the caller can decode doc-pack progress
// anchors — a progress row whose legacy `chapter_id`/`tts_id`/`tts_batch_version`
// are null. Absent/false → the server ALWAYS returns legacy chapter-anchored
// progress (or null), so old strict-decoding clients (the prod Flutter app)
// never crash on those nulls.
// v2: apps up to 6.0.x send the original `doc_pack_progress` but resume from
// the start whenever a row carries a doc-pack anchor and they play chapters,
// so the server ignores that flag and they get chapter anchors only.
// @see apps/api/src/api-impl/audio-endpoints.ts (docPackProgressAllowedFor)
const DocPackProgressCapabilityField = {
  doc_pack_progress_v2: Schema.optional(DocPackProgressBooleanFromString),
} as const;

const DocPackProgressUrlParams = Schema.Struct(DocPackProgressCapabilityField);

export const ListAudioUrlParams = Schema.Struct({
  ...DocPackProgressCapabilityField,
  cursor: Schema.optional(Schema.String).annotate({
    description: 'Pagination cursor for the next page',
  }),
  limit: Schema.optional(Schema.FiniteFromString).annotate({
    description: 'Number of audio items to return (default 20)',
  }),
  status: Schema.optional(
    Schema.Union([
      ConversionStatusSchema,
      Schema.Array(ConversionStatusSchema),
    ]),
  ).annotate({
    description: 'Filter by conversion status',
  }),
  except_status: Schema.optional(
    Schema.Union([
      ConversionStatusSchema,
      Schema.Array(ConversionStatusSchema),
    ]),
  ).annotate({
    description: 'Exclude by conversion status',
  }),
  sort_by: Schema.optional(
    Schema.Literals(['created_at', 'played_at', 'title']),
  ).annotate({
    description:
      'Sort by `created_at` (default, descending), `played_at` (descending, unplayed last) or `title` (ascending, untitled last)',
  }),
  sort_dir: Schema.optional(Schema.Literals(['asc', 'desc'])).annotate({
    description:
      'Sort direction. Defaults to descending for `created_at` and `played_at` and ascending for `title`; rows without the sort key stay last either way',
  }),
  folder_id: Schema.optional(Schema.String).annotate({
    description:
      'Return only conversions in this folder (not recursive). Use "root" for top-level conversions.',
  }),
  title_query: Schema.optional(Schema.String).annotate({
    description:
      'Return only conversions whose title contains this text, case- and surrounding-whitespace-insensitive (the same folding `sort_by=title` orders on, so untitled conversions never match). Empty or whitespace-only applies no filter.',
  }),
  if_modified_since_ms: Schema.optional(Schema.FiniteFromString).annotate({
    description:
      'Only include conversions updated after this timestamp (ms since epoch)',
  }),
});

const unarchiveEndpoint = HttpApiEndpoint.post('UnarchiveAudio', '/unarchive', {
  payload: Schema.Struct({
    audioId: Schema.String,
  }),
  success: Schema.Void.pipe(HttpApiSchema.status(200)),
  error: InternalServerError,
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Unarchive Audio')
  .annotate(
    OpenApi.Description,
    'Unarchive an audio conversion for the current user',
  );

const listEndpoint = HttpApiEndpoint.get('ListAudio', '/', {
  query: ListAudioUrlParams,
  success: GetListAudioV2200.pipe(HttpApiSchema.status(200)),
  // NotFoundError is reachable only via `folder_id` — a caller-supplied id for a
  // folder that isn't theirs (or doesn't exist). Additive for existing clients:
  // they never send an unknown folder_id, so they never see it.
  error: [NotFoundError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'List Audio')
  .annotate(
    OpenApi.Description,
    'List all audio conversions for the current user',
  );

const DocumentUrlSource = Schema.Struct({
  document_url: Schema.String.annotate({
    description: 'URL of the document to convert',
  }),
});

const IntegrationSource = Schema.Struct({
  integration_source: Schema.Union([
    Schema.Struct({
      provider: Schema.Literal('zotero'),
      file_id: Schema.String,
    }),
    Schema.Struct({
      provider: Schema.Literal('dropbox'),
      file_id: Schema.String,
    }),
    Schema.Struct({
      provider: Schema.Literal('google-drive'),
      connection_id: Schema.optional(Schema.String),
      resource_key: Schema.optional(Schema.String),
      file_id: Schema.String,
    }),
  ]),
});

const CreateAudioCommon = Schema.Struct({
  document_type: Schema.optional(DocumentTypeSchema).annotate({
    description:
      'Document type. If not provided, will be auto-detected from URL or Content-Type.',
  }),
  title: Schema.optional(Schema.String).annotate({
    description:
      'Optional title for the audio. If not provided, will be extracted from document.',
  }),
  folder_id: Schema.optional(Schema.NullOr(Schema.String)).annotate({
    description: 'Optional ID of the folder to add this conversion to',
  }),
  // Dev/staging-only: lets the web app pick the conversion pipeline for this one conversion,
  // overriding the global default. Existing clients omit it.
  // @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
  conversion_pipeline_version: Schema.optional(
    Schema.Literals(['v1', 'v2']),
  ).annotate({
    description:
      'Conversion pipeline version to use (v1 or v2). Defaults to the server-configured pipeline if omitted.',
  }),
  // Dev/staging-only: run this one conversion WITHOUT the vision layout model (an empty-but-valid
  // vision layout). Previews M7 — deleting the vision model — on real staging uploads. Existing
  // clients omit it. @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
  conversion_vision_off: Schema.optional(Schema.Boolean).annotate({
    description:
      'Run the conversion without the vision layout model (dev/staging only). Defaults to false.',
  }),
  // Dev/staging-only v2 variant switches (no-ops on v1). Absent = server config decides.
  // @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
  conversion_ocr_provider: Schema.optional(
    Schema.Literals(['azure', 'paddle-modal', 'paddleocr-vl-modal']),
  ).annotate({
    description:
      'OCR provider for the v2 pipeline (dev/staging only). Defaults to the server-configured provider.',
  }),
  conversion_label_model: Schema.optional(
    Schema.Literals(['finetune', 'general']),
  ).annotate({
    description:
      'Label step model for the v2 pipeline (dev/staging only): the fine-tuned 8B or the configured general LLM.',
  }),
  conversion_update_mode: Schema.optional(
    Schema.Literals(['finetune', 'general', 'deterministic', 'hybrid']),
  ).annotate({
    description:
      'Update step mode for the v2 pipeline (dev/staging only): fine-tuned 8B, configured general LLM, the deterministic normalizer, or hybrid (normalizer plus the general LLM for sections with math).',
  }),
  conversion_word_enrichment: Schema.optional(Schema.Boolean).annotate({
    description:
      'Force the v2 word-geometry enrichment stage on (true) or off (false) (dev/staging only). Defaults to the server config.',
  }),
});

const CreateAudioPayload = Schema.Union([
  DocumentUrlSource.pipe(Schema.fieldsAssign(CreateAudioCommon.fields)),
  IntegrationSource.pipe(Schema.fieldsAssign(CreateAudioCommon.fields)),
]);

const createEndpoint = HttpApiEndpoint.post('CreateAudio', '/', {
  payload: CreateAudioPayload,
  success: PostCreateAudioV2200.pipe(HttpApiSchema.status(201)),
  error: [
    BadRequestError,
    FileTooLargeError,
    DocumentInaccessibleError,
    UploadQuotaExceededError,
    UploadDocumentTypeNotAllowedError,
    UnauthorizedError,
    IntegrationReconnectRequiredError,
    BadGatewayError,
    ServiceUnavailableError,
    InternalServerError,
  ],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Create Audio')
  .annotate(
    OpenApi.Description,
    'Start a new audio conversion from a document URL or integration source',
  );

const getAudioEndpoint = HttpApiEndpoint.get('GetAudio', '/:audioOrSlug', {
  params: Schema.Struct({
    audioOrSlug: Schema.String.annotate({
      description: 'Audio ID or URL slug',
    }),
  }),
  query: DocPackProgressUrlParams,
  success: GetGetAudioV2200.pipe(HttpApiSchema.status(200)),
  error: [NotFoundError, UnauthorizedError, InternalServerError],
})
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Audio')
  .annotate(
    OpenApi.Description,
    'Get a single audio conversion by ID or URL slug. Public audios can be accessed without authentication.',
  );

const getAudioDetailedEndpoint = HttpApiEndpoint.get(
  'GetAudioDetailed',
  '/:audioOrSlug/detailed',
  {
    params: Schema.Struct({
      audioOrSlug: Schema.String.annotate({
        description: 'Audio ID or URL slug',
      }),
    }),
    query: DocPackProgressUrlParams,
    success: GetGetAudioDetailedV2200.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      UnauthorizedError,
      AudioRechunkingError,
      InternalServerError,
    ],
  },
)
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Audio (Detailed)')
  .annotate(
    OpenApi.Description,
    'Get a single audio conversion with chapter summaries and voice info. Public audios can be accessed without authentication.',
  );

// Dev/staging-only: per-step conversion cost/timing diagnostics for one audio.
// Server-gated to non-production (returns 404 in prod). See web-app-tooling.md §6.
const getConversionDiagnosticsEndpoint = HttpApiEndpoint.get(
  'GetConversionDiagnostics',
  '/:audioId/conversion-diagnostics',
  {
    params: Schema.Struct({
      audioId: Schema.String.annotate({ description: 'Audio ID' }),
    }),
    success: GetConversionDiagnosticsV2200.pipe(HttpApiSchema.status(200)),
    error: [NotFoundError, UnauthorizedError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Conversion Diagnostics (dev/staging)')
  .annotate(
    OpenApi.Description,
    'Dev/staging-only per-step conversion cost/timing rows for an audio. Returns 404 in production.',
  );

// Dev/staging-only: the resolved server config defaults for the conversion settings page.
// Server-gated to non-production (returns 404 in prod). Static segment, so it wins over the
// parametric `/:audioOrSlug` routes. See web-app-tooling.md §3.
const getConversionDefaultsEndpoint = HttpApiEndpoint.get(
  'GetConversionDefaults',
  '/conversion-defaults',
  {
    success: GetConversionDefaultsV2200.pipe(HttpApiSchema.status(200)),
    error: [NotFoundError, UnauthorizedError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Conversion Defaults (dev/staging)')
  .annotate(
    OpenApi.Description,
    'Dev/staging-only resolved server-side conversion config defaults. Returns 404 in production.',
  );

const GetAudioPageImageResponse = Schema.Struct({
  url: Schema.String,
  page: Schema.Int,
  width: Schema.optional(Schema.Int),
  height: Schema.optional(Schema.Int),
  format: Schema.Literals(['webp', 'png']),
  cache_status: Schema.Literals(['hit', 'miss']),
});

const getAudioPageImageEndpoint = HttpApiEndpoint.get(
  'GetAudioPageImage',
  '/:audioOrSlug/page-image/:page',
  {
    params: Schema.Struct({
      audioOrSlug: Schema.String.annotate({
        description: 'Audio ID or URL slug',
      }),
      page: Schema.FiniteFromString.annotate({
        description: '1-based PDF page number',
      }),
    }),
    success: GetAudioPageImageResponse.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      UnauthorizedError,
      BadRequestError,
      InternalServerError,
    ],
  },
)
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Audio Page Image')
  .annotate(
    OpenApi.Description,
    'Get or lazily render a cached page image for a PDF-backed audio conversion.',
  );

export const UpdateAudioPayload = Schema.Struct({
  title: Schema.optional(Schema.NullOr(Schema.String)),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  tags: Schema.optional(
    Schema.NullOr(
      Schema.Array(
        Schema.Struct({ label: Schema.String, color: Schema.String }),
      ),
    ),
  ),
  visibility: Schema.optional(Schema.NullOr(SharingInfoVisibility)),
  shared_with_emails: Schema.optional(
    Schema.NullOr(Schema.Array(Schema.String)),
  ),
  shared_with_user_ids: Schema.optional(
    Schema.NullOr(Schema.Array(Schema.String)),
  ),
  // Client capability signal (optional = additive, freeze-safe). Mirrors the
  // GET endpoints' `doc_pack_progress_v2` urlParam: `true` ⇒ the caller can decode
  // the returned doc-pack progress anchors (null legacy chapter_id/tts_id).
  // Absent ⇒ legacy anchors only. Does NOT change what this endpoint updates.
  doc_pack_progress_v2: Schema.optional(Schema.Boolean),
});

const UpdateAudioResponse = Schema.Struct({
  audio_conversion: AudioConversionSummary,
});

const updateEndpoint = HttpApiEndpoint.patch('UpdateAudio', '/:audioId', {
  params: Schema.Struct({
    audioId: Schema.String.annotate({
      description: 'Audio ID',
    }),
  }),
  payload: UpdateAudioPayload,
  success: UpdateAudioResponse.pipe(HttpApiSchema.status(200)),
  error: [NotFoundError, BadRequestError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Update Audio')
  .annotate(OpenApi.Description, 'Update properties of an audio conversion');

const UpdateAudioProgressPayload = Schema.Struct({
  progress_age_ms: Schema.optional(
    Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  ),
  timestamp_ms: Schema.Int.annotate({
    description: 'Current playback position in milliseconds',
  }),
  chapter_id: Schema.NullOr(Schema.String)
    .pipe(Schema.withDecodingDefaultType(Effect.succeed(null)))
    .annotate({
      description:
        'Compatibility chapter anchor. doc-pack-backed clients may omit this.',
    }),
  tts_id: Schema.NullOr(Schema.String)
    .pipe(Schema.withDecodingDefaultType(Effect.succeed(null)))
    .annotate({
      description: 'Legacy TTS chunk id, when progress is anchored to a chunk',
    }),
  doc_pack_page: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  doc_pack_char_start: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
  doc_pack_char_end: Schema.NullOr(Schema.Int).pipe(
    Schema.withDecodingDefaultType(Effect.succeed(null)),
  ),
});

const updateProgressEndpoint = HttpApiEndpoint.put(
  'UpdateAudioProgress',
  '/:audioId/progress',
  {
    params: Schema.Struct({
      audioId: Schema.String.annotate({
        description: 'Audio ID',
      }),
    }),
    payload: UpdateAudioProgressPayload,
    success: Schema.Void.pipe(HttpApiSchema.status(204)),
    error: [NotFoundError, BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Update Audio Progress')
  .annotate(
    OpenApi.Description,
    'Set the current audio position. Supports doc-pack page/char anchors for the new reader path.',
  );

const FinalizeAudioResponse = Schema.Struct({
  audio_conversion: AudioConversionSummary,
});

const finalizeEndpoint = HttpApiEndpoint.post(
  'FinalizeAudio',
  '/:audioId/finalize',
  {
    params: Schema.Struct({
      audioId: Schema.String.annotate({
        description: 'Audio ID',
      }),
    }),
    payload: Schema.Struct({
      voice: VoiceInput.annotate({
        description:
          'Voice ID to synthesize with (accepts display name or model_id, any case)',
      }),
      // Client capability signal (optional = additive, freeze-safe). Mirrors the
      // GET endpoints' `doc_pack_progress_v2` urlParam: `true` ⇒ the caller can
      // decode the returned doc-pack progress anchors (null legacy
      // chapter_id/tts_id). Absent ⇒ legacy anchors only.
      doc_pack_progress_v2: Schema.optional(Schema.Boolean),
      rebuild: Schema.optional(Schema.Boolean).annotate({
        description:
          'Build a new archive even if a completed one exists, e.g. when the completed archive does not cover the chapters the client has',
      }),
    }),
    success: FinalizeAudioResponse.pipe(HttpApiSchema.status(200)),
    error: [NotFoundError, BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Finalize Audio')
  .annotate(
    OpenApi.Description,
    'Pre-generate all TTS audio for offline mobile listening',
  );

export const MAX_FINALIZATION_STATUS_IDS = 100;

const finalizationStatusEndpoint = HttpApiEndpoint.post(
  'GetFinalizationStatus',
  '/finalization-status',
  {
    payload: Schema.Struct({
      audio_ids: Schema.Array(Schema.String).pipe(
        Schema.check(Schema.isMaxLength(MAX_FINALIZATION_STATUS_IDS)),
      ),
    }),
    success: Schema.Struct({
      items: Schema.Array(
        Schema.Struct({
          id: Schema.String,
          finalized_metadata: Schema.NullOr(AudioConversionFinalizedMetadata),
        }),
      ),
    }).pipe(HttpApiSchema.status(200)),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Finalization Status')
  .annotate(
    OpenApi.Description,
    "Finalization status of the caller's audio conversions, without chapters. Ids the caller cannot see are omitted.",
  );

export const OfflineDocPackBuild = Schema.Struct({
  plan_hash: Schema.String,
  status: Schema.Literals(['pending', 'processing', 'completed', 'failed']),
  manifest_url: Schema.NullOr(Schema.String),
  version: Schema.Int,
});
export type OfflineDocPackBuild = typeof OfflineDocPackBuild.Type;

const prepareOfflineDocPackEndpoint = HttpApiEndpoint.post(
  'PrepareOfflineDocPack',
  '/:audioId/offline-doc-pack',
  {
    params: Schema.Struct({
      audioId: Schema.String.annotate({ description: 'Audio ID' }),
    }),
    payload: Schema.Struct({
      voice: VoiceInput,
      pack_url: Schema.String,
      content_version: Schema.Int,
      chunks: Schema.Array(
        Schema.Array(SynthesizeDocPackRange).pipe(
          Schema.check(
            Schema.isMinLength(1),
            Schema.isMaxLength(SYNTHESIZE_MAX_RANGES),
          ),
        ),
      ).pipe(
        Schema.check(
          Schema.isMinLength(1),
          Schema.isMaxLength(OFFLINE_DOC_PACK_MAX_CHUNKS),
        ),
      ),
      rebuild: Schema.optional(Schema.Boolean),
    }),
    success: OfflineDocPackBuild.pipe(HttpApiSchema.status(200)),
    error: [NotFoundError, BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Prepare Offline Doc-Pack')
  .annotate(
    OpenApi.Description,
    'Synthesize every chunk the client will play offline, given as doc-pack page/char ranges, and build a manifest of their audio and word timings.',
  );

const offlineDocPackStatusEndpoint = HttpApiEndpoint.post(
  'GetOfflineDocPackStatus',
  '/offline-doc-pack-status',
  {
    payload: Schema.Struct({
      builds: Schema.Array(
        Schema.Struct({ audio_id: Schema.String, plan_hash: Schema.String }),
      ).pipe(Schema.check(Schema.isMaxLength(MAX_FINALIZATION_STATUS_IDS))),
    }),
    success: Schema.Struct({
      items: Schema.Array(
        Schema.Struct({
          audio_id: Schema.String,
          build: OfflineDocPackBuild,
        }),
      ),
    }).pipe(HttpApiSchema.status(200)),
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Offline Doc-Pack Status')
  .annotate(
    OpenApi.Description,
    "Status of the caller's offline doc-pack builds. Builds the caller cannot see are omitted.",
  );

const deleteEndpoint = HttpApiEndpoint.delete('DeleteAudio', '/:audioId', {
  params: Schema.Struct({
    audioId: Schema.String.annotate({
      description: 'Audio ID',
    }),
  }),
  success: Schema.Void.pipe(HttpApiSchema.status(204)),
  error: [NotFoundError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Delete Audio')
  .annotate(
    OpenApi.Description,
    'Soft-delete an audio conversion for the current user',
  );

const listChaptersEndpoint = HttpApiEndpoint.get(
  'ListChapters',
  '/:audioOrSlug/chapters',
  {
    params: Schema.Struct({
      audioOrSlug: Schema.String.annotate({
        description: 'Audio ID or URL slug',
      }),
    }),
    query: ListChaptersUrlParams,
    success: GetListAudioChaptersV2200.pipe(HttpApiSchema.status(200)),
    error: [
      NotFoundError,
      UnauthorizedError,
      AudioRechunkingError,
      InternalServerError,
    ],
  },
)
  .middleware(OptionalUserAuthMiddleware)
  .annotate(OpenApi.Summary, 'List Chapters (legacy)')
  .annotate(OpenApi.Deprecated, true)
  .annotate(
    OpenApi.Description,
    'Legacy chapter transport with paragraph/sentence/word details and stored TTS ids. New readers should use GetAudioDetailed for metadata plus the doc-pack/page-image projections.',
  );

const getSavedDocumentInsightsEndpoint = HttpApiEndpoint.get(
  'GetSavedDocumentInsights',
  '/:audioId/insights/saved',
  {
    params: Schema.Struct({ audioId: Schema.String }),
    query: DocumentInsightsSelection,
    success: Schema.NullOr(SavedDocumentInsights),
    error: [NotFoundError, UnauthorizedError, InternalServerError],
  },
).middleware(UserAuthMiddleware);

const ensureDocumentInsightsEndpoint = HttpApiEndpoint.post(
  'EnsureDocumentInsights',
  '/:audioId/insights/saved',
  {
    params: Schema.Struct({ audioId: Schema.String }),
    payload: EnsureDocumentInsightsRequest,
    success: SavedDocumentInsights,
    error: [
      NotFoundError,
      UnauthorizedError,
      BadRequestError,
      InternalServerError,
      RateLimitedError,
    ],
  },
).middleware(UserAuthMiddleware);

export const audioGroup = HttpApiGroup.make('Audio')
  .add(getSavedDocumentInsightsEndpoint)
  .add(ensureDocumentInsightsEndpoint)
  .add(unarchiveEndpoint)
  .add(listEndpoint)
  .add(createEndpoint)
  .add(updateEndpoint)
  .add(updateProgressEndpoint)
  .add(finalizeEndpoint)
  .add(finalizationStatusEndpoint)
  .add(prepareOfflineDocPackEndpoint)
  .add(offlineDocPackStatusEndpoint)
  .add(deleteEndpoint)
  .add(getAudioEndpoint)
  .add(getAudioDetailedEndpoint)
  .add(getAudioPageImageEndpoint)
  .add(listChaptersEndpoint)
  .add(getConversionDiagnosticsEndpoint)
  .add(getConversionDefaultsEndpoint)
  .prefix('/v2/audio');
